import { Box } from '@mui/material'
import { DataSentryEventsTable } from './DataSentryEventsTable'
import { DataCategoriesSharing } from './dataTabComponents/DataCategoriesSharing'
import { DataSentryDataClassificationEventsTable } from './DataSentryDataClassificationEventsTable'
import {
  ClassificationType,
  AuthorizationStatus as EventsAuthorizationStatus,
} from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { useDataSentryEventsByClassification } from 'api/dataSentry/queries/useDataSentryEventsByClassification'
import { useDataSentryContext } from '../context/DataSentryContext'
import { DataSentrySensitivityChart } from './DataSentrySensitivityChart'
import { SpectrumRecharts } from './dataTabComponents/OverallSpectrumChart'
import { useMemo } from 'react'
import { Card, Icon, theme, IconDictionary } from '@ketch-com/deck'
import { formatToK } from '../utils/transformPieChartData'
import pluralize from 'pluralize'
import { DataClassificationDetails } from 'pages/dataSentry/data/dataClassificationDetails/DataClassificationDetails'

export const DataTab: React.FC = () => {
  const { selectedTimeRange, selectedPropertyCode, eventsSummary, isEventsSummaryLoading } = useDataSentryContext()
  const { data: eventsByClassification, isLoading } = useDataSentryEventsByClassification({
    params: {
      'classification-type': ClassificationType.DATA_SENSITIVITY,
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
    },
  })

  const categoryData = useMemo(() => {
    if (!eventsSummary?.classificationTypes?.length) {
      return { categoryCount: 0, totalCategoryCount: 0 }
    }
    const dataCategoryObj = eventsSummary.classificationTypes.find(item => item.classificationType === 'DATA_CATEGORY')

    if (dataCategoryObj) {
      return {
        categoryCount: dataCategoryObj.classificationCount || 0,
        totalCategoryCount: dataCategoryObj.totalClassificationCount || 0,
      }
    }
  }, [eventsSummary?.classificationTypes])

  const unauthorizedCollectionEvents = eventsSummary?.authorizationTypes?.find(
    event => event.status === EventsAuthorizationStatus.UNAUTHORIZED,
  )
  const unauthorizedCollectionEventsCount = unauthorizedCollectionEvents?.count || 0

  return (
    <Box
      width="100%"
      sx={{
        overflowX: 'auto',
        maxWidth: '1280px',
      }}
      mx="auto"
      display="flex"
      flexDirection="column"
      gap={6}
      pb={16}
      mt={3}
    >
      <Box display="flex" gap={3}>
        <SpectrumRecharts
          maxCategoryCount={categoryData?.totalCategoryCount}
          categoryCount={categoryData?.categoryCount || 0}
          isLoading={isEventsSummaryLoading}
        />
        <Box display="flex" flexDirection="column" flexBasis={260} gap={2}>
          <Card
            cardVariant="stretchWidth"
            cardTitle={formatToK(eventsSummary?.total || 0)}
            subTitle={pluralize('Collection Event', eventsSummary?.total)}
            primaryIcon={
              <Icon
                name={IconDictionary.OTransformDown}
                width="32px"
                height="32px"
                iconColor={theme.palette.Black.o48}
              />
            }
            isNumerical
            backgroundColor={eventsSummary?.total ? theme.palette.White.o100 : 'transparent'}
            textColor={eventsSummary?.total ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
            sx={{
              border: eventsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
              padding: 2,
            }}
          />

          <Card
            cardVariant="stretchWidth"
            cardTitle={formatToK(unauthorizedCollectionEventsCount || 0)}
            subTitle={pluralize('Unauthorized Collection Event', unauthorizedCollectionEventsCount)}
            primaryIcon={
              <Icon
                name={IconDictionary.FTransformDown}
                width="32px"
                height="32px"
                iconColor={theme.palette.Black.o48}
              />
            }
            isNumerical
            backgroundColor={unauthorizedCollectionEventsCount ? theme.palette.Black.o8 : 'transparent'}
            textColor={unauthorizedCollectionEventsCount ? theme.palette.Text.Primary : theme.palette.Text.Secondary}
            sx={{
              border: eventsSummary?.total ? 'none' : `1px solid ${theme.palette.Black.o8}`,
              padding: 2,
            }}
          />
        </Box>

        <DataSentrySensitivityChart
          eventsByClassification={eventsByClassification?.classifications || []}
          isLoading={isLoading}
        />
      </Box>

      <DataCategoriesSharing />

      <Box display="flex" justifyContent="space-between">
        <DataSentryDataClassificationEventsTable classificationType={ClassificationType.DATA_CATEGORY} />

        <DataSentryDataClassificationEventsTable classificationType={ClassificationType.DATA_TYPE} />
      </Box>

      <DataSentryEventsTable />

      <DataClassificationDetails />
    </Box>
  )
}
